import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ICompany } from 'interfaces/companies';
import { getCompanies } from 'services/companies';
import { yandexCheckCompanyRights } from 'services/yandex';
import { RootState } from 'store';
import { selectActiveProductSlug } from 'store/productsReducer';

interface CompaniesState {
	companies: ICompany[];
	activeCompany: ICompany | null;
	hasCompanyRights: boolean;
};

const initialState: CompaniesState = {
	companies: [],
	activeCompany: null,
	hasCompanyRights: false,
};

export const fetchCompanies = createAsyncThunk<ICompany[]>(
	'companies/fetchCompanies',
	async () => {
		const { data: companiesData } = await getCompanies();
		return companiesData?.items || [];
	}
);

export const refreshCompanyData = createAsyncThunk<void, void,
	{
		state: RootState
	}
>(
	'companies/refreshCompanyData',
	async (_, { dispatch, getState }) => {
		const companies = await dispatch(fetchCompanies()).unwrap();
		const store: RootState = getState();
		const activeCompanyId = store.companies.activeCompany?.id;
		const newActiveCompany = companies.find((company) => `${company.id}` === `${activeCompanyId}`) || null;
		dispatch(setActiveCompany(newActiveCompany));
	}
);

export const fetchCompanyRights = createAsyncThunk(
	'companies/fetchCompanyRights',
	async ({ companyId, subclientId }: { companyId: string | number, subclientId: string | number }) => {
		const { data: companyRights } = await yandexCheckCompanyRights({
			companyId: companyId,
			subclientId: subclientId
		});
		return companyRights.owned;
	}
);

export const companiesSlice = createSlice({
	name: 'companies',
	initialState,
	reducers: {
		setActiveCompany: (state, action: PayloadAction<ICompany | null>) => {
			state.activeCompany = action.payload
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchCompanies.fulfilled, (state, action) => {
				state.companies = action.payload;
			})
			.addCase(fetchCompanies.rejected, (state) => {
				state.companies = [];
			})
			.addCase(fetchCompanyRights.fulfilled, (state, action) => {
				state.hasCompanyRights = action.payload;
			})
			.addCase(fetchCompanyRights.rejected, (state) => {
				state.hasCompanyRights = false;
			})
	},
});

export const { setActiveCompany } = companiesSlice.actions;

export const selectCompanies = (state: RootState) => state.companies.companies;
export const selectActiveCompany = (state: RootState) => state.companies.activeCompany;
export const selectHasCompanyRights = (state: RootState) => state.companies.hasCompanyRights;
export const selectActiveCompanyId = createSelector(
	selectActiveCompany,
	(activeCompany) => activeCompany?.id || ''
);
export const selectCompanyProduct = createSelector(
	selectActiveCompany,
	selectActiveProductSlug,
	(activeCompany, activeProductSlug) => activeCompany?.partners?.[activeProductSlug]
);
export const selectCompanyProductId = createSelector(
	selectCompanyProduct,
	(companyProduct) => companyProduct?.id
);
export const selectCompanyProductSubclient = createSelector(
	selectActiveCompany,
	selectActiveProductSlug,
	(activeCompany, activeProductSlug) => activeCompany?.subclients?.[activeProductSlug]?.[0]
);
export const selectCompanyProductSubclientId = createSelector(
	selectCompanyProductSubclient,
	(companyProductSubclient) => companyProductSubclient?.id || ''
);
export const selectCompanyProductSubclientLogin = createSelector(
	selectCompanyProductSubclient,
	(companyProductSubclient) => companyProductSubclient?.login || ''
);

export default companiesSlice.reducer;
